import { useTheme } from '@simplywallst/ui-core';
import { useChartScales } from '../../hooks/useChartScales';
import { useChartDimensions } from '../../hooks/useChartDimensions';
import { normalizeChartData } from '../../utils/normaliseChartData';
import { usePriceHistoryData } from './hooks/usePriceHistoryData';
import type { MiniPriceChartProps } from '../../types';
import { Wrapper } from './styled';
import { ChartStatus } from '../ChartStatus';
import { ChartContent } from '../ChartContent';

export const NarrativeMiniPriceChart = ({
  companyId,
  period = '1Y',
  height = 68,
  align = 'right',
  includeXAxis = false,
  interactive = true,
  valuations,
  onHover,
  baselineType = 'average',
}: MiniPriceChartProps) => {
  const theme = useTheme();

  // Get chart dimensions and wrapper ref
  const { dimensions, wrapperRef } = useChartDimensions({
    height,
    includeXAxis,
  });

  // Get the price history data from API
  const { data, periodMap, isNegativeMovement, status } = usePriceHistoryData(
    companyId,
    period
  );

  const { keys, entities, movement } = data;

  // Normalize data to percentages
  const { normalisedEntities, normalisedValuations } = normalizeChartData(
    entities,
    keys,
    valuations,
    baselineType
  );

  // Set up scales
  const { xScale, yScale } = useChartScales({
    dimensions,
    keys,
    normalisedEntities,
    normalisedValuations,
    periodStart: periodMap[period],
    periodEnd: periodMap.NOW,
  });

  const positionX = align === 'left' && keys.length > 0 ? -xScale(keys[0]) : 0;

  return (
    <Wrapper
      as="div"
      ref={wrapperRef}
      style={{
        width: `calc(100% + ${dimensions.paddingX * 2}px)`,
        overflow: 'hidden',
      }}
    >
      <ChartStatus status={status} height={height} hasData={keys.length > 0} />

      {status === 'success' && keys.length > 0 && (
        <ChartContent
          height={height}
          keys={keys}
          normalisedEntities={normalisedEntities}
          normalisedValuations={normalisedValuations}
          periods={periodMap}
          period={period}
          positionX={positionX}
          xScale={xScale}
          yScale={yScale}
          dimensions={dimensions}
          interactive={interactive}
          onHover={onHover}
          fillColor={movement}
          strokeColor={isNegativeMovement ? theme.color.bad : theme.color.good}
        />
      )}
    </Wrapper>
  );
};
